import styled from 'styled-components';
import { mq } from '~/styles/grid';
import { HeadlineBasic } from '../../../atoms/Headline/HeadlineSkins';
import { CopyTextBasicStyling } from '../../../atoms/Copytext/Copytext.styles';
import TeaserTileImage, { teaserTileImgHeightMap } from './TeaserTileImage';
import TeaserTileLinkList from './TeaserTileLinkList';
import getFontFamily from '~/helpers/getFontFamily';
import SafeHTML from '../../../atoms/SafeHTML';
import { CopyStandardSkin } from '../../../atoms/Copytext/CopytextSkins';
import CTA from '../../../atoms/CTA';
import { CTAIconPosition } from '../../../atoms/CTA/CTA.constants';

const Content = styled.div`
  height: ${(props) =>
    props.hasImage ? `calc(100% - ${props.height}px)` : '100%'};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${(props) =>
    props.type === 'LARGE' && props.parentContainerType === undefined
      ? '40px 65px 30px'
      : '40px 25px 25px'};

  .iconBefore svg {
    color: ${(props) => props.theme.color.primary800} !important;
  }

  &&& a:hover span.text {
    color: ${(props) => props.theme.color.cta} !important;
  }

  @media (${mq.smDown}) {
    padding: 40px 25px 25px;

    height: ${(props) =>
      props.hasImage
        ? `calc(100% - ${teaserTileImgHeightMap.SMALL}px)`
        : '100%'};
  }
`;

const HeadlineStyled = styled.div`
  ${HeadlineBasic};
  font-family: ${(props) => {
    if (props.theme.teaserTile && props.theme.teaserTile.headlineFont) {
      return getFontFamily(props.theme.teaserTile.headlineFont);
    }
    if (props.theme.headline.h500.xl.font) {
      return getFontFamily(props.theme.headline.h500.xl.font);
    }
    return getFontFamily();
  }};
  font-size: ${(props) => props.theme.headline.h500.xl.fontSize};
  line-height: ${(props) => props.theme.headline.h500.xl.lineHeight};

  @media (${mq.xs}) {
    font-size: ${(props) => props.theme.headline.h600.xs.fontSize};
    line-height: ${(props) => props.theme.headline.h600.xs.lineHeight};
  }
`;

const marginCta = 36;

const Text = styled.div`
  ${CopyTextBasicStyling};
  ${CopyStandardSkin};
  margin: 15px 0 ${marginCta}px;

  * {
    color: ${(props) => props.theme.color.gray600} !important;
  }

  h2,
  h3,
  h4,
  h5 {
    color: ${(props) => props.theme.color.basicHeadlineColor} !important;
  }

  ul li {
    &:before {
      top: 12px;
    }
  }
`;

const CtaDummy = styled.div`
  display: flex;
  margin-top: ${(props) => (props.hasText ? 0 : marginCta)}px;
  justify-content: space-between;
  align-items: center;
`;

/**
 * @param {TeaserTileContentProps} props
 * @returns {React.ReactElement} The rendered component
 */
const TeaserTileContent = ({
  isHovered,
  image,
  type,
  linkList,
  headline,
  text,
  textV2,
  readmoreText,
  isClickable = false,
  parentContainerType,
}) => {
  const imgHeight = teaserTileImgHeightMap[type];

  return (
    <>
      {image && <TeaserTileImage image={image} type={type} />}
      <Content
        type={type}
        hasImage={!!image}
        height={imgHeight}
        parentContainerType={parentContainerType}
        data-id="teaser-tile-content"
      >
        <div>
          <HeadlineStyled data-id="teaser-tile-content-headline">
            {headline}
          </HeadlineStyled>
          {text && !textV2 && (
            <Text data-id="teaser-tile-content-text-1">{text}</Text>
          )}
          {textV2 && (
            <SafeHTML
              as={Text}
              data-id="teaser-tile-content-text-2"
              html={textV2}
            />
          )}
          {linkList && linkList[0] && (
            <TeaserTileLinkList linkList={linkList} />
          )}
        </div>
        {isClickable && (
          <CtaDummy
            hasText={(!!text && text !== '') || (!!textV2 && textV2 !== '')}
            data-id="teaser-tile-content-cta-container"
          >
            <CTA
              skin="TERTIARY"
              icon="ARROW_ANIMATION"
              text={readmoreText}
              title={readmoreText}
              iconPos={CTAIconPosition.After}
              parentIsHovered={isHovered}
              $type={type}
              $parentContainerType={parentContainerType}
            />
          </CtaDummy>
        )}
      </Content>
    </>
  );
};

export default TeaserTileContent;
